import
{
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { PopupService } from "@service/popup/popup.service";
import { WindowRefService } from "@service/window-service/window-ref.service";
import { formatGpsrPostalAddress, GpsrEconomicOperator, GpsrView } from "app/model/gpsr.model";

/**
 * Modal z danymi wymaganymi przez art. 19 GPSR (issue #1710).
 *
 * Renderuje DWIE role, bo tego wymaga rozporządzenie: producenta (zawsze) oraz —
 * gdy producent nie ma siedziby w UE — dodatkowo osobę odpowiedzialną w UE.
 * To nie jest alternatywa, oba bloki potrafią wystąpić naraz.
 *
 * Cykl życia (otwieranie, Esc, przycisk wstecz, blokada scrolla, rejestracja w
 * `PopupService`) jest celowo odwzorowany 1:1 z `TooltipPopupComponent` — to modal
 * „Data wysyłki", wskazany przez biznes jako wzorzec. Nie dziedziczymy po nim,
 * bo tamten komponent jest zrośnięty z kalendarzem wysyłki i drugim popupem statusu.
 */
@Component({
  selector: "app-gpsr-popup",
  templateUrl: "./gpsr-popup.component.html",
  styleUrls: ["./gpsr-popup.component.scss"],
})
export class GpsrPopupComponent implements OnInit, OnDestroy
{
  @Input() public name: string = "gpsr";
  @Input() public view: GpsrView = null;

  @Output() public readonly emitAsClose: EventEmitter<void> = new EventEmitter<void>();

  public showForm = false;
  public loaded = false;

  constructor(
    private windowRef: WindowRefService,
    private changeDet: ChangeDetectorRef,
    private popupService: PopupService,
  )
  {
  }

  public postalAddress(operator: GpsrEconomicOperator): string
  {
    return formatGpsrPostalAddress(operator);
  }

  /**
   * `contactUrl` przychodzi bez schematu (zmierzone: `www.arte.pl`), więc dokładamy
   * `https://` — bez tego przeglądarka potraktowałaby wartość jako ścieżkę względną.
   */
  public contactHref(operator: GpsrEconomicOperator): string
  {
    const url = operator && operator.contactUrl;

    if (!url)
    {
      return "";
    }

    return /^https?:\/\//i.test(url) ? url : "https://" + url;
  }

  public openModal(disableReload?: boolean): void
  {
    this.popupService.openPopup(this.name, !!disableReload);
    this.showForm = true;
    this.windowRef.blockScroll();
    this.loaded = true;
    this.changeDet.detectChanges();
  }

  public closeModal(event?: any, disableReload?: boolean): void
  {
    if (!this.popupService.isActivePopup(this.name))
    {
      return;
    }

    this.showForm = false;
    this.loaded = false;
    this.popupService.closePopup(this.name, !!disableReload);
    this.emitAsClose.emit();
    this.changeDet.detectChanges();

    // Scroll odblokowujemy dopiero po zamknięciu — tak samo jak TooltipPopupComponent.
    setTimeout(() =>
    {
      this.windowRef.unBlockScroll();
    }, 100);
  }

  @HostListener("window:keyup", ["$event"])
  private onKeyUp(event): void
  {
    if (this.showForm && event.key === "Escape")
    {
      this.closeModal(null);
    }
  }

  @HostListener("window:popstate", ["$event"])
  private backButtonHandler(): void
  {
    if (!this.popupService.isActiveParam("m", this.name) && this.popupService.isActivePopup(this.name))
    {
      this.closeModal(null, true);
    }
  }

  public ngOnInit(): void
  {
    this.popupService.reserveName(this.name);
  }

  public ngOnDestroy(): void
  {
    this.popupService.clearName(this.name);
  }
}
